import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { brandProfiles } from "@/mocks/brandsData";
import { useSEO } from "@/hooks/useSEO";

const distributors = brandProfiles.filter((b) => b.relationship === "distributor");
const techPartners = brandProfiles.filter((b) => b.relationship === "technology-partner");

export default function BrandsPage() {
  useSEO({
    title: "Technology Partners Malaysia | Hytera, Arcfra, ExaGrid Authorized Distributor | SE Distribution",
    description: "SE Distribution is the authorized distributor for Hytera, Arcfra & ExaGrid in Malaysia, and technology partner for Honeywell, Dell Rugged & Synology. Explore our full partner ecosystem.",
    canonical: "https://www.sedistribution.com.my/brands",
    keywords: "technology partners Malaysia, authorized distributor Malaysia, Hytera Malaysia, Arcfra Malaysia, ExaGrid Malaysia, Honeywell Malaysia, Dell Rugged Malaysia, Synology Malaysia",
  });
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "distributor" | "technology-partner">("all");

  const filtered =
    activeTab === "all"
      ? brandProfiles
      : brandProfiles.filter((b) => b.relationship === activeTab);

  const selectedBrand = activeBrand
    ? brandProfiles.find((b) => b.id === activeBrand)
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[360px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=collection%20of%20enterprise%20technology%20brands%20hardware%20products%20arranged%20together%20server%20storage%20camera%20device%20professional%20display%20showcase%2C%20clean%20minimal%20light%20background%2C%20professional%20commercial%20photography%2C%20sharp%20detail&width=1400&height=600&seq=brands-hero&orientation=landscape"
          alt="Technology Partners"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/15"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-6">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Technology Partners</span>
          </nav>
          <span className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">Our Ecosystem</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-2xl">
            Technology Partners
          </h1>
          <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-6">
            SE Distribution is the <strong className="text-white">authorized distributor</strong> for Hytera, Arcfra &amp; ExaGrid — and a trusted <strong className="text-white">technology partner</strong> for 3 additional world-class brands across Malaysia.
          </p>

          {/* Ecosystem Badge Strip */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <div className="flex items-center gap-2.5 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-verified-badge-fill text-white text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">{distributors.length} Authorized Distributor</p>
                <p className="text-xs font-semibold text-white leading-none">Hytera &middot; Arcfra &middot; ExaGrid</p>
              </div>
            </div>
            <div className="hidden sm:flex w-px h-8 bg-white/20"></div>
            <div className="flex items-center gap-2.5 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-shake-hands-fill text-white/70 text-sm"></i>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 leading-none mb-0.5">{techPartners.length} Technology Partner</p>
                <p className="text-xs font-semibold text-white leading-none">Synology &middot; Honeywell &middot; Dell Rugged Pro</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">{distributors.length}</p>
            <p className="text-xs text-white/50 uppercase tracking-wider">Authorized Distributorships</p>
          </div>
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">{techPartners.length}</p>
            <p className="text-xs text-white/50 uppercase tracking-wider">Technology Partnerships</p>
          </div>
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">12+</p>
            <p className="text-xs text-white/50 uppercase tracking-wider">Years Partnership Experience</p>
          </div>
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">6</p>
            <p className="text-xs text-white/50 uppercase tracking-wider">Industries Served</p>
          </div>
        </div>
      </section>

      {/* Relationship Distinction Banner */}
      <section className="py-10 px-6 lg:px-20 bg-[#f9f7f4] border-b border-gray-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-6 border border-gray-100 flex items-start gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
              <i className="ri-verified-badge-line text-white text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-[#1a1a1a] text-sm">Authorized Distributor</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-2 py-0.5 rounded-full">{distributors.length} Brands</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong className="text-[#1a1a1a]">Hytera, Arcfra &amp; ExaGrid</strong> — SE Distribution holds official authorized distributor agreements for these brands in Malaysia. This means genuine products, full manufacturer warranty, direct escalation paths, and certified local support.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-gray-100 flex items-start gap-4">
            <div className="w-11 h-11 flex items-center justify-center bg-[#f5f3ee] rounded-lg flex-shrink-0">
              <i className="ri-shake-hands-line text-[#1a1a1a] text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-[#1a1a1a] text-sm">Technology Partner</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#f5f3ee] text-gray-600 px-2 py-0.5 rounded-full">{techPartners.length} Brands</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong className="text-[#1a1a1a]">Synology, Honeywell &amp; Dell Rugged Pro</strong> — SE Distribution works as a technology partner, integrating these solutions into our offerings to deliver complete, end-to-end solutions for our clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 py-4 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          {[
            { key: "all", label: `All Partners (${brandProfiles.length})` },
            { key: "distributor", label: `Authorized Distributor (${distributors.length})` },
            { key: "technology-partner", label: `Technology Partner (${techPartners.length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key as typeof activeTab); setActiveBrand(null); }}
              className={`whitespace-nowrap text-xs font-medium px-4 py-2 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                activeTab === tab.key
                  ? "bg-[#1a1a1a] text-white"
                  : "bg-[#f5f3ee] text-gray-600 hover:bg-[#eae8e3]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Authorized Distributors Section */}
      {(activeTab === "all" || activeTab === "distributor") && (
        <section className="py-14 px-6 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {activeTab === "all" && (
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
                  <i className="ri-verified-badge-line text-white text-sm"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1a1a1a]">Authorized Distributor</h2>
                  <p className="text-xs text-gray-400">Official distributor agreements — genuine products, full warranty, direct manufacturer support</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(activeTab === "all" ? distributors : filtered).map((brand) => (
                <BrandCard
                  key={brand.id}
                  brand={brand}
                  isActive={activeBrand === brand.id}
                  onToggle={() => setActiveBrand(activeBrand === brand.id ? null : brand.id)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Technology Partners Section */}
      {(activeTab === "all" || activeTab === "technology-partner") && (
        <section className={`py-14 px-6 lg:px-20 ${activeTab === "all" ? "bg-[#f9f7f4] border-t border-gray-100" : ""}`}>
          <div className="max-w-7xl mx-auto">
            {activeTab === "all" && (
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 flex items-center justify-center bg-[#f5f3ee] rounded-lg border border-gray-200">
                  <i className="ri-shake-hands-line text-[#1a1a1a] text-sm"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1a1a1a]">Technology Partners</h2>
                  <p className="text-xs text-gray-400">Integrated into our solutions ecosystem to deliver complete end-to-end capabilities</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {(activeTab === "all" ? techPartners : filtered).map((brand) => (
                <BrandCard
                  key={brand.id}
                  brand={brand}
                  isActive={activeBrand === brand.id}
                  onToggle={() => setActiveBrand(activeBrand === brand.id ? null : brand.id)}
                  compact={activeTab === "all"}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Brand Detail Drawer */}
      {selectedBrand && (
        <section className="bg-[#f8f7f4] border-t border-gray-200 py-16 px-6 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {/* Relationship Badge */}
            <div className="mb-8 flex items-center gap-3">
              {selectedBrand.relationship === "distributor" ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-3 py-1.5 rounded-full">
                  <i className="ri-verified-badge-line"></i> Authorized Distributor — Malaysia
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#f5f3ee] text-gray-700 border border-gray-200 px-3 py-1.5 rounded-full">
                  <i className="ri-shake-hands-line"></i> Technology Partner
                </span>
              )}
            </div>

            {/* Header */}
            <div className="flex flex-col lg:flex-row gap-10 items-start mb-12">
              <div className="w-full lg:w-2/5 h-72 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={selectedBrand.coverImage}
                  alt={selectedBrand.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2 block">{selectedBrand.category}</span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-2">{selectedBrand.name}</h2>
                <p className="text-gray-500 text-base mb-5 italic">{selectedBrand.tagline}</p>
                <div className="flex flex-wrap gap-4 mb-5 text-sm text-gray-600">
                  <span><strong className="text-[#1a1a1a]">Origin:</strong> {selectedBrand.origin}</span>
                  <span><strong className="text-[#1a1a1a]">Founded:</strong> {selectedBrand.founded}</span>
                  <span>
                    <strong className="text-[#1a1a1a]">Website:</strong>{" "}
                    <a href={`https://${selectedBrand.website}`} target="_blank" rel="nofollow noreferrer" className="underline hover:text-[#1a1a1a]">
                      {selectedBrand.website}
                    </a>
                  </span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{selectedBrand.description}</p>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Key Highlights</p>
                  <ul className="space-y-2">
                    {selectedBrand.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Arcfra-specific: Gartner recognition + spotlight link */}
            {selectedBrand.id === "arcfra" && (
              <div className="mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Analyst Recognition</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  {[
                    { icon: "ri-award-line", title: "Gartner March 2025", desc: "\"Guide to Choosing a VMware Alternative in the Wake of Broadcom Acquisition\"" },
                    { icon: "ri-award-line", title: "Gartner April 2025", desc: "1 of 8 worldwide representative vendors — \"Market Guide for Full-Stack HCI Software\"" },
                    { icon: "ri-medal-line", title: "DCIG Top 5 2026–27", desc: "Top 5 VMware Alternatives — Rising Vendors Edition" },
                  ].map((badge) => (
                    <div key={badge.title} className="flex items-start gap-3 bg-[#1a1a1a] rounded-lg px-4 py-3.5">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-md flex-shrink-0">
                        <i className={`${badge.icon} text-white text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">{badge.title}</p>
                        <p className="text-[10px] text-white/55 leading-relaxed mt-0.5">{badge.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/arcfra-spotlight"
                  className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  View Full Arcfra AECP Spotlight
                </Link>
              </div>
            )}

            {/* Products */}
            <div className="mb-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">Featured Products</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {selectedBrand.products.map((prod) => (
                  <div key={prod.name} className="bg-white rounded-lg p-5">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#f5f3ee] rounded-md mb-3">
                      <i className={`${prod.icon} text-lg text-[#1a1a1a]`}></i>
                    </div>
                    <h4 className="font-bold text-[#1a1a1a] text-sm mb-1">{prod.name}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{prod.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases + CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Ideal For</p>
                <div className="flex flex-wrap gap-2">
                  {selectedBrand.useCases.map((uc) => (
                    <span key={uc} className="text-xs font-medium bg-[#1a1a1a] text-white px-3 py-1.5 rounded-full">
                      {uc}
                    </span>
                  ))}
                </div>
              </div>
              <div className="sm:ml-auto flex-shrink-0">
                <Link
                  to="/contact"
                  className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  Enquire about {selectedBrand.name} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why We Partner / How We Work */}
      <section className="py-20 px-6 lg:px-20 bg-[#f9f7f4] border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Our Approach</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-4">
              How Technology Partners Are Integrated Into Our Solutions
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              When SE Distribution proposes a solution, we don't just sell products — we architect complete ecosystems. Here's how our technology partner brands are woven into every proposal.
            </p>
          </div>

          {/* Process Flow */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 mb-16 relative">
            {[
              {
                step: "01",
                icon: "ri-search-eye-line",
                title: "Needs Assessment",
                desc: "We start by understanding your operational environment, pain points, compliance requirements, and budget — before recommending any product.",
              },
              {
                step: "02",
                icon: "ri-puzzle-line",
                title: "Solution Architecture",
                desc: "Our engineers design a complete solution architecture — selecting the right mix of authorized distributor products and technology partner integrations to meet your exact requirements.",
              },
              {
                step: "03",
                icon: "ri-shake-hands-line",
                title: "Partner Integration",
                desc: "Technology partner brands like Honeywell, Dell Rugged, and Synology are integrated as complementary layers — filling capability gaps and enhancing the core solution.",
              },
              {
                step: "04",
                icon: "ri-customer-service-2-line",
                title: "Unified Support",
                desc: "SE Distribution is your single point of contact for the entire solution — regardless of how many brands are involved. One team, one SLA, one escalation path.",
              },
            ].map((item, i) => (
              <div key={item.step} className="relative flex flex-col items-center text-center px-6">
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] right-0 h-px bg-gray-200 z-0"></div>
                )}
                <div className="relative z-10 w-16 h-16 flex items-center justify-center bg-white border-2 border-[#1a1a1a] rounded-full mb-5">
                  <i className={`${item.icon} text-xl text-[#1a1a1a]`}></i>
                </div>
                <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mb-1">{item.step}</span>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Partner Role Cards */}
          <div className="mb-14">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-6 text-center">How Each Technology Partner Fits Into Our Solutions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  brand: "Honeywell",
                  icon: "ri-smartphone-line",
                  solution: "Rugged Computing",
                  role: "Honeywell rugged tablets and mobile computers are integrated into our field operations and warehouse solutions — providing the scanning, connectivity, and durability layer that enterprise workflows demand.",
                  tags: ["Warehousing", "Logistics", "Field Service"],
                  link: "/solutions/rugged-computing",
                },
                {
                  brand: "Dell Rugged Pro",
                  icon: "ri-macbook-line",
                  solution: "Rugged Computing",
                  role: "Dell Rugged Pro laptops and tablets are proposed for defence, engineering, and field deployments where full Windows capability and MIL-SPEC protection are both required simultaneously.",
                  tags: ["Defence", "Field Engineering", "Construction"],
                  link: "/solutions/rugged-computing",
                },
                {
                  brand: "Synology",
                  icon: "ri-cloud-line",
                  solution: "HCI & Storage",
                  role: "Synology NAS and Active Backup Suite are integrated as the SMB-friendly storage and backup layer in our data protection stack — complementing Arcfra HCI and ExaGrid for complete coverage.",
                  tags: ["Backup", "NAS", "Hybrid Cloud"],
                  link: "/solutions/data-protection",
                },

              ].map((item) => (
                <div key={item.brand} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#f5f3ee] rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-lg text-[#1a1a1a]`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1a1a1a] text-sm">{item.brand}</h4>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{item.solution}</span>
                    </div>
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-[#f5f3ee] text-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">Tech Partner</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">{item.role}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {item.tags.map((tag) => (
                      <span key={tag} className="text-[10px] font-medium bg-[#1a1a1a]/5 text-[#1a1a1a] px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <a href={item.link} className="text-xs font-semibold text-[#1a1a1a] flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    See in action <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Why This Model Works */}
          <div className="bg-[#1a1a1a] rounded-xl p-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">Why This Model Works</span>
              <h3 className="text-2xl font-bold text-white mb-4 leading-tight">
                Best-of-Breed Solutions Without the Integration Headache
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                By combining our authorized distributor brands with carefully selected technology partners, SE Distribution can propose the best-fit solution for any requirement — without being locked into a single vendor's ecosystem. Our clients get genuine products, certified support, and a unified team managing the entire solution lifecycle.
              </p>
              <a href="/contact" className="inline-flex items-center gap-2 bg-white text-[#1a1a1a] text-sm font-semibold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Discuss Your Requirements <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-shield-check-line", title: "No Vendor Lock-In", desc: "We propose what's right for you, not what's easiest for us to sell" },
                { icon: "ri-team-line", title: "Single Point of Contact", desc: "One team manages the full solution regardless of brands involved" },
                { icon: "ri-verified-badge-line", title: "Genuine Products", desc: "Authorized distributor brands come with full manufacturer warranty" },
                { icon: "ri-tools-line", title: "Local Support", desc: "Certified engineers in Malaysia for all brands in our ecosystem" },
              ].map((item) => (
                <div key={item.title} className="bg-white/5 rounded-lg p-4">
                  <div className="w-8 h-8 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-lg text-white/70`}></i>
                  </div>
                  <h4 className="font-bold text-white text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Authorized */}
      <section className="py-20 px-6 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Why It Matters</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-5">
                Distributor vs Technology Partner — What&apos;s the Difference?
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                As the <strong>authorized distributor</strong> for Hytera, Arcfra, and ExaGrid, SE Distribution holds official agreements that grant us direct access to manufacturer pricing, warranty programs, certified spare parts, and escalation support. These are brands we stock, sell, and fully support in Malaysia.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Our <strong>technology partners</strong> — Synology, Honeywell, and Dell Rugged Pro — are integrated into our solution ecosystem. We work with these brands to deliver complete, end-to-end solutions where their technology complements our core offerings, ensuring our clients always get the best-fit solution.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-verified-badge-line", title: "Genuine Products", desc: "Authorized distributor products carry full manufacturer warranty and serial number verification" },
                  { icon: "ri-customer-service-2-line", title: "Certified Support", desc: "Brand-certified engineers with direct manufacturer escalation for distributor brands" },
                  { icon: "ri-puzzle-line", title: "Integrated Solutions", desc: "Technology partner brands are woven into complete solution architectures" },
                  { icon: "ri-file-list-3-line", title: "Full Documentation", desc: "Authorized invoices, proof of purchase, and import compliance for enterprise procurement" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 p-4 bg-[#f8f7f4] rounded-lg">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-lg text-[#1a1a1a]`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1a1a1a] text-sm mb-0.5">{item.title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full h-96 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20partnership%20handshake%20enterprise%20technology%20company%20authorized%20distributor%20meeting%2C%20modern%20office%20setting%2C%20dramatic%20lighting%2C%20realistic%20professional%20photography&width=700&height=600&seq=brands-authorized&orientation=portrait"
                alt="Authorized distribution partnership"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1a1a1a] py-20 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Looking for a Specific Brand or Product?
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto">
            Our team is ready to help you find the right product from our authorized brands or technology partner ecosystem. Send us your requirements and we&apos;ll respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              Send an Enquiry <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/solutions"
              className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Our Solutions
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface BrandCardProps {
  brand: (typeof brandProfiles)[0];
  isActive: boolean;
  onToggle: () => void;
  compact?: boolean;
}

function BrandCard({ brand, isActive, onToggle, compact = false }: BrandCardProps) {
  return (
    <button
      onClick={onToggle}
      className={`text-left rounded-lg overflow-hidden border transition-all cursor-pointer group w-full ${
        isActive
          ? "border-[#1a1a1a] ring-1 ring-[#1a1a1a]"
          : "border-gray-100 hover:border-gray-200 bg-[#f8f7f4]"
      }`}
    >
      {!compact && (
        <div className="w-full h-36 overflow-hidden bg-gray-50">
          <img
            src={brand.coverImage}
            alt={brand.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className={compact ? "p-4" : "p-4"}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 truncate pr-2">
            {brand.relationship === "distributor" ? (
              <span className="text-[#1a1a1a] font-bold">Authorized Distributor</span>
            ) : (
              "Technology Partner"
            )}
          </span>
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className={`${brand.icon} text-base text-gray-400`}></i>
          </div>
        </div>
        <h3 className="font-bold text-[#1a1a1a] text-base">{brand.name}</h3>
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{brand.tagline}</p>
        <div className="mt-3 flex items-center gap-1 text-xs text-[#1a1a1a] font-medium">
          {isActive ? (
            <>Hide details <i className="ri-arrow-up-s-line"></i></>
          ) : (
            <>View profile <i className="ri-arrow-down-s-line"></i></>
          )}
        </div>
      </div>
    </button>
  );
}
